import { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useGetAssetAssignments } from "@hooks/use-get/asset-assignments";
import { Loader } from "@components/loader";
import { formatDate } from "@utils/format-utils";
import { UserIdentifierDisplay } from "@components/user-identifier-display";

interface AssetAssignmentHistoryTabProps {
  /** Fetch history for a specific asset. */
  assetId?: number;
  /** Fetch history for a specific user. */
  wpUserId?: number;
}

export const AssetAssignmentHistoryTab = ({
  assetId,
  wpUserId,
}: AssetAssignmentHistoryTabProps) => {
  const { assignments, loading, getByAsset, getByUser } =
    useGetAssetAssignments();

  const fetchHistory = () => {
    if (assetId) getByAsset(assetId);
    else if (wpUserId) getByUser(wpUserId);
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, wpUserId]);

  if (loading) return <Loader message="Loading assignment history..." />;

  if (assignments.length === 0) {
    return (
      <Typography variant="body1" sx={{ textAlign: "center", my: 2 }}>
        No assignment history found.
      </Typography>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small" aria-label="assignment history">
          <TableHead>
            <TableRow>
              {assetId != null && <TableCell>Assigned To</TableCell>}
              {wpUserId != null && <TableCell>Asset ID</TableCell>}
              <TableCell>Assigned By</TableCell>
              <TableCell>Assigned At</TableCell>
              <TableCell>Unassigned At</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.map((row) => (
              <TableRow key={row.id}>
                {assetId != null && (
                  <TableCell>
                    {row.assigned_to_name ? (
                      <UserIdentifierDisplay
                        firstName={row.assigned_to_name.split(" ")[0]}
                        lastName={row.assigned_to_name.split(" ").slice(1).join(" ")}
                        role={row.assigned_to_role}
                        grade={row.assigned_to_grade}
                      />
                    ) : "Unknown User"}
                  </TableCell>
                )}
                {wpUserId != null && <TableCell>{row.asset_id}</TableCell>}
                <TableCell>
                  {row.assigned_by_name ? (
                    <UserIdentifierDisplay
                      firstName={row.assigned_by_name.split(" ")[0]}
                      lastName={row.assigned_by_name.split(" ").slice(1).join(" ")}
                      role={row.assigned_by_role}
                      grade={row.assigned_by_grade}
                    />
                  ) : (row.assigned_by != null ? "Unknown User" : "—")}
                </TableCell>
                <TableCell>{formatDate(row.assigned_at)}</TableCell>
                <TableCell>
                  {row.unassigned_at ? (
                    formatDate(row.unassigned_at)
                  ) : (
                    <Typography variant="caption" color="success.main">
                      Active
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{row.notes ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
